/**
 * Sprint Redesign v1 · Phase 2+ · Baustein 1 (Do 03.09.2026, 15:15).
 *
 * Theme-Toggle hell/dunkel für die Top-Bar. Quick-Patch als Motivations-Boost.
 *
 * Implementierung:
 *   - Tailwind `darkMode: "class"` (bereits konfiguriert in tailwind.config.js)
 *   - Toggle setzt/entfernt `dark`-Class auf <html>
 *   - Persistenz via localStorage("f4s_theme": "light" | "dark")
 *   - System-Preference als Default (matchMedia prefers-color-scheme)
 *
 * data-testid: theme-toggle
 */
import React, { useEffect, useState, useCallback } from "react";
import { Sun, Moon } from "lucide-react";

const STORAGE_KEY = "f4s_theme";

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
    root.setAttribute("data-theme", "dark");
  } else {
    root.classList.remove("dark");
    root.setAttribute("data-theme", "light");
  }
}

function readInitialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch (_) { /* no-op */ }
  try {
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  } catch (_) { /* no-op */ }
  return "light";
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const initial = readInitialTheme();
    setTheme(initial);
    applyTheme(initial);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      applyTheme(next);
      try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* no-op */ }
      return next;
    });
  }, []);

  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle"
      data-theme={theme}
      aria-label={isDark ? "Zum hellen Modus wechseln" : "Zum dunklen Modus wechseln"}
      aria-pressed={isDark}
      title={isDark ? "Zum hellen Modus" : "Zum dunklen Modus"}
      className="rounded-full w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800 transition-colors"
    >
      {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}
